/**
 * Selection store (Zustand): which rows are selected in the workspace folder
 * on screen, plus the anchor a shift-click range extends from, and the one
 * item being renamed in place (`renaming`), scoped the same way.
 *
 * Scoped to one `{workspaceId, folderId}`. `setScope` with a different scope
 * clears the selection, and `clearWorkspaceQueries` (the workspace switch and
 * sign-out path) calls `clear`, so a selection never outlives the rows it was
 * made on. Deliberately NOT persisted: a stale selection restored on reload
 * would aim a bulk action at rows the user no longer sees.
 *
 * Contract for consumers: the items an action runs on are
 * `flatItems ∩ selectedIds` (the loaded rows, filtered by the ids), NEVER
 * the ids alone. An id can outlive its row inside one scope (a /sync refresh
 * drops a row another client moved or deleted, a filter hides it), and only
 * the row carries what `resolveSelectionActions` and the bulk requests need
 * (kind, parent, `canEdit`). Acting on bare ids would aim at rows the user
 * can no longer see.
 */

import { create } from 'zustand';

export interface SelectionScope {
  readonly workspaceId: string;
  /** null = the workspace root. */
  readonly folderId: string | null;
}

/**
 * Which host draws an item: the canvas views, or the sidebar tree. Both can
 * show the same item, so each keeps its own context menu (`itemMenuStore`)
 * and only the surface an in-place rename started on edits it.
 */
export type ItemSurface = 'canvas' | 'tree';

interface SelectionState {
  scope: SelectionScope | null;
  selectedIds: ReadonlySet<string>;
  anchorId: string | null;
  /** The one item being renamed in place (InlineRenameField), or null. */
  renaming: { readonly id: string; readonly surface: ItemSurface } | null;
  startRename: (id: string, surface: ItemSurface) => void;
  /**
   * Ends `id`'s rename. A no-op once another rename has started, so a slow
   * save that finishes late never closes the newer one.
   */
  stopRename: (id: string) => void;
  setScope: (scope: SelectionScope) => void;
  toggle: (id: string) => void;
  selectOnly: (id: string) => void;
  /** Shift-click: select anchor..id over `orderedIds` (the rows as displayed). */
  selectRange: (id: string, orderedIds: readonly string[]) => void;
  selectAll: (loadedIds: readonly string[]) => void;
  /** Replace the selection with `ids` and range from `anchorId` (a marquee). */
  setSelection: (ids: Iterable<string>, anchorId: string | null) => void;
  clear: () => void;
}

const EMPTY = { selectedIds: new Set<string>(), anchorId: null, renaming: null };

export const useSelectionStore = create<SelectionState>()((set, get) => ({
  scope: null,
  ...EMPTY,
  setScope: (scope) => {
    const current = get().scope;
    if (current?.workspaceId === scope.workspaceId && current.folderId === scope.folderId) return;
    set({ scope, ...EMPTY });
  },
  toggle: (id) =>
    set(({ selectedIds }) => {
      const next = new Set(selectedIds);
      if (!next.delete(id)) next.add(id);
      return { selectedIds: next, anchorId: id };
    }),
  selectOnly: (id) => set({ selectedIds: new Set([id]), anchorId: id }),
  selectRange: (id, orderedIds) => {
    const { anchorId } = get();
    const from = anchorId === null ? -1 : orderedIds.indexOf(anchorId);
    const to = orderedIds.indexOf(id);
    if (from === -1 || to === -1) {
      set({ selectedIds: new Set([id]), anchorId: id });
      return;
    }
    const [start, end] = from <= to ? [from, to] : [to, from];
    set({ selectedIds: new Set(orderedIds.slice(start, end + 1)) });
  },
  selectAll: (loadedIds) => set({ selectedIds: new Set(loadedIds) }),
  setSelection: (ids, anchorId) => set({ selectedIds: new Set(ids), anchorId }),
  startRename: (id, surface) => set({ renaming: { id, surface } }),
  stopRename: (id) => {
    if (get().renaming?.id === id) set({ renaming: null });
  },
  clear: () => set({ scope: null, ...EMPTY }),
}));
